import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowRight, ArrowLeft } from 'lucide-react';
import OtpVerificationDialog from '@/components/OtpVerificationDialog';
import { setAuthToken } from '@/lib/axiosAuth';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const RegisterForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Step management
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Phone + OTP
  
  // Step 1 - Basic Info
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Step 2 - Phone
  const [phone, setPhone] = useState('');
  const [registerAs, setRegisterAs] = useState('user');
  
  // OTP
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // Step 1 Validation
  const validateStep1 = () => {
    if (!name || name.length < 2) {
      toast.error('Lütfen ad soyad giriniz (en az 2 karakter)');
      return false;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Geçerli bir email adresi giriniz');
      return false;
    }
    if (!password || password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return false;
    }
    if (!acceptTerms) {
      toast.error('Devam etmek için sözleşmeyi kabul etmelisiniz');
      return false;
    }
    return true;
  };

  // Move to Step 2
  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (validateStep1()) {
      setStep(2);
    }
  };

  // Step 2 - Phone + Register
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.length < 10) {
      toast.error('Geçerli bir telefon numarası giriniz');
      return;
    }
    
    setLoading(true);
    
    try {
      // Send registration request with OTP
      await axios.post(`${API}/auth/register`, {
        name,
        email,
        password,
        phone,
        register_as: registerAs
      }, { withCredentials: true });
      
      toast.success('Doğrulama kodu gönderildi');
      setShowOtpDialog(true);
      
    } catch (error) {
      console.error('Register error:', error);
      const errorMsg = error.response?.data?.detail;
      // Handle validation errors array
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Kayıt başlatılamadı');
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Kayıt başlatılamadı');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code) => {
    setVerifyingOtp(true);
    try {
      const response = await axios.post(`${API}/auth/verify-register`, {
        phone: phone,
        code: code
      }, { withCredentials: true });
      
      // Persist token for Bearer auth
      if (response.data?.token) {
        setAuthToken(response.data.token);
      }
      setShowOtpDialog(false);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      if (response.data.role === 'operator') {
        toast.success('İşletmeci kaydınız tamamlandı!');
        navigate('/isletmeci');
      } else {
        toast.success('Kayıt başarılı! Hoş geldiniz.');
        navigate(location.state?.from || '/hesabim');
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Doğrulama başarısız');
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Doğrulama başarısız');
      }
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <>
      {step === 1 ? (
        <form onSubmit={handleStep1Submit} className="space-y-4">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Hesap Oluştur</h3>
            <p className="text-sm text-slate-500 mt-1">Hızlıca kayıt olun ve rezervasyon yapın</p>
          </div>

          {/* Ad Soyad */}
          <div>
            <Label htmlFor="name">Ad Soyad</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="name"
                type="text"
                placeholder="Ad Soyad"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10"
                required
                autoFocus
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="ornek@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          {/* Şifre */}
          <div>
            <Label htmlFor="password">Şifre</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="En az 6 karakter"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Sözleşme */}
          <div className="flex items-start space-x-2 py-2">
            <Checkbox 
              id="terms" 
              checked={acceptTerms}
              onCheckedChange={setAcceptTerms}
              className="mt-0.5"
            />
            <label
              htmlFor="terms"
              className="text-sm text-slate-600 leading-tight cursor-pointer"
            >
              <a href="/kullanim-kosullari" target="_blank" className="text-emerald-700 hover:underline">
                Kullanım Koşulları
              </a>
              {' '}ve{' '}
              <a href="/gizlilik-politikasi" target="_blank" className="text-emerald-700 hover:underline">
                Gizlilik Politikası
              </a>
              'nı okudum ve kabul ediyorum.
            </label>
          </div>

          <Button 
            type="submit"
            className="w-full bg-emerald-700 hover:bg-emerald-800 h-11"
          >
            Devam Et
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      ) : (
        <form onSubmit={handleFinalSubmit} className="space-y-4">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Telefon Doğrulama</h3>
            <p className="text-sm text-slate-500 mt-1">SMS ile doğrulama için telefon numaranızı girin</p>
          </div>

          {/* Telefon */}
          <div>
            <Label htmlFor="phone">Telefon Numarası</Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="phone"
                type="tel"
                placeholder="05XX XXX XX XX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pl-10"
                required
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Doğrulama kodu bu numaraya gönderilecektir</p>
          </div>

          {/* Kayıt Türü */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <Label className="text-sm mb-2 block">Kayıt türünüz nedir?</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setRegisterAs('user')}
                className={`flex-1 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                  registerAs === 'user'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                👤 Müşteri
              </button>
              <button
                type="button"
                onClick={() => setRegisterAs('operator')}
                className={`flex-1 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                  registerAs === 'operator'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                🏢 İşletmeci
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              type="button"
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Geri
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className="flex-1 bg-emerald-700 hover:bg-emerald-800"
            >
              {loading ? 'Gönderiliyor...' : 'Doğrulama Kodu Gönder'}
            </Button>
          </div>
        </form>
      )}

      {/* OTP Dialog */}
      <OtpVerificationDialog 
        isOpen={showOtpDialog}
        phone={phone}
        onVerify={handleVerifyOtp}
        onCancel={() => setShowOtpDialog(false)}
        loading={verifyingOtp}
      />
    </>
  );
};

export default RegisterForm;
